import React, { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';
import useDebounced from '~/hooks/useDebounced';

import type { Tag } from '~/services/TagsService';

import { TAG_TYPE_OPTIONS, TagTypeValue } from './constants';
import { useEndTag, useGetTags, useGetTagsMetadata } from './hooks';
import { DATAGRID_HIDE_SEPARATORS_SX } from './styles';

const EXPORT_TAG_TYPE_ALL = 'all';
type ExportTagType = typeof EXPORT_TAG_TYPE_ALL | TagTypeValue;

interface Props {
  onCreate: () => void;
  onEdit: (tagId: string) => void;
  onViewContent: (tagName: string) => void;
}

export default function TagsList({ onCreate, onEdit, onViewContent }: Props) {
  const [endTagId, setEndTagId] = useState<string | null>(null);
  const [tagNameFilter, setTagNameFilter] = useState('');
  const [tagTypeFilter, setTagTypeFilter] = useState<TagTypeValue | ''>('');
  const [reportTagType, setReportTagType] = useState<ExportTagType>(EXPORT_TAG_TYPE_ALL);
  const endTag = useEndTag();
  const debouncedTagNameFilter = useDebounced(tagNameFilter.trim(), 300);

  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 100,
    page: 0,
  });

  const {
    data = null,
    isLoading = false,
    error = null,
  } = useGetTags({
    offset: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    sortBy: 'updated_at',
    sortOrder: 'desc',
    name: debouncedTagNameFilter || undefined,
    tagType: tagTypeFilter || undefined,
  });
  const tagIds = data?.result?.map((tag) => tag.id) ?? [];
  const {
    assignmentCounts,
    scheduleSummaries,
    inactiveTagIds,
    isLoading: isTagsMetadataLoading,
  } = useGetTagsMetadata(tagIds);

  const sortedRows = useMemo(() => {
    const rows = data?.result ?? [];
    if (isTagsMetadataLoading || inactiveTagIds.size === 0) {
      return rows;
    }

    const active = rows.filter((tag) => !inactiveTagIds.has(tag.id));
    const inactive = rows.filter((tag) => inactiveTagIds.has(tag.id));
    return [...active, ...inactive];
  }, [data?.result, inactiveTagIds, isTagsMetadataLoading]);

  const createCountColumn = (field: string, headerName: string): GridColDef<Tag> => ({
    field,
    headerName,
    minWidth: 160,
    sortable: false,
    renderCell: (params: GridRenderCellParams<Tag>) => {
      const counts = assignmentCounts.get(params.row.id);
      if (isTagsMetadataLoading && !counts) {
        return <Box sx={{ width: '100%', py: 1 }}>...</Box>;
      }
      return <Box sx={{ width: '100%', py: 1 }}>{counts?.[field as keyof typeof counts] ?? 0}</Box>;
    },
    display: 'flex',
  });

  const handleTagTypeFilterChange = (event: SelectChangeEvent<TagTypeValue | ''>) => {
    setTagTypeFilter(event.target.value as TagTypeValue | '');
    setPaginationModel((currentModel) => ({ ...currentModel, page: 0 }));
  };

  const handleTagNameFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTagNameFilter(event.target.value);
    setPaginationModel((currentModel) => ({ ...currentModel, page: 0 }));
  };

  const columns: Array<GridColDef<Tag>> = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 160,
      flex: 1,
      renderCell: (params: GridRenderCellParams<Tag>) => (
        <Box sx={{ width: '100%', py: 1 }}>
          <Tooltip title={params.row.id}>
            <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {params.row.name}
            </Box>
          </Tooltip>
        </Box>
      ),
      display: 'flex',
    },
    {
      field: 'tagType',
      headerName: 'Type',
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<Tag>) => {
        const tagType = TAG_TYPE_OPTIONS.find((option) => option.value === params.row.tagType);

        return <Box sx={{ width: '100%', py: 1 }}>{tagType?.label ?? params.row.tagType}</Box>;
      },
      display: 'flex',
    },
    createCountColumn('destinations', 'Destinations'),
    createCountColumn('hotels', 'Hotels'),
    createCountColumn('experiences', 'Experiences'),
    {
      field: 'schedules',
      headerName: 'Schedules',
      minWidth: 200,
      flex: 2,
      sortable: false,
      cellClassName: 'tags-schedules-cell',
      renderCell: (params: GridRenderCellParams<Tag>) => {
        const schedules = scheduleSummaries.get(params.row.id);

        if (isTagsMetadataLoading && !schedules) {
          return <Box sx={{ width: '100%', py: 1 }}>Loading schedules...</Box>;
        }

        if (!schedules || schedules.length === 0) {
          return <Box sx={{ width: '100%', py: 1 }}>No schedules</Box>;
        }

        return (
          <Stack spacing={0.5} sx={{ width: '100%', py: 1 }}>
            {schedules.map((schedule) => (
              <Box key={schedule.id} sx={{ display: 'block', whiteSpace: 'normal' }}>
                {schedule.text}
              </Box>
            ))}
          </Stack>
        );
      },
      display: 'flex',
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 160,
      width: 240,
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      cellClassName: 'tags-edit-cell',
      renderCell: (params: GridRenderCellParams<Tag>) => (
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} width="100%" height="100%">
          <Button
            variant="text"
            onClick={(event) => {
              event.stopPropagation();
              onEdit(params.row.id);
            }}
          >
            Edit
          </Button>
          <Button
            variant="text"
            color="success"
            onClick={(event) => {
              event.stopPropagation();
              onViewContent(params.row.name);
            }}
          >
            Content
          </Button>
          <Button
            variant="text"
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              setEndTagId(params.row.id);
            }}
            disabled={endTag.isPending}
          >
            End
          </Button>
        </Box>
      ),
      display: 'flex',
    },
  ];

  return (
    <Container sx={{ pt: 3 }}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
          Create Tag
        </Button>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1, justifyContent: 'center' }}>
          <TextField
            label="Search by name"
            size="small"
            value={tagNameFilter}
            onChange={handleTagNameFilterChange}
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="tag-type-filter-label">Tag type</InputLabel>
            <Select
              labelId="tag-type-filter-label"
              label="Tag type"
              value={tagTypeFilter}
              onChange={handleTagTypeFilterChange}
            >
              <MenuItem value="">All tag types</MenuItem>
              {TAG_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            select
            label="Export tag type"
            size="small"
            value={reportTagType}
            onChange={(event) => setReportTagType(event.target.value as ExportTagType)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value={EXPORT_TAG_TYPE_ALL}>All</MenuItem>
            {TAG_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={() => window.open(`/marketing/tag-reports/export?tagType=${reportTagType}`, '_blank')}
          >
            Export Tags Data
          </Button>
        </Stack>
      </Box>
      {!!error && <Alert severity="error">{error?.message}</Alert>}
      <DataGrid
        loading={isLoading}
        rows={sortedRows}
        columns={columns}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row: Tag) => row.id}
        onRowClick={(params) => onEdit(params.row.id)}
        paginationMode="server"
        rowCount={data?.pagination?.total_items ?? 0}
        slots={{ pagination: GridPagination }}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        getRowHeight={() => 'auto'}
        getRowClassName={(params) => (inactiveTagIds.has(params.row.id) ? 'tags-row--inactive' : '')}
        sx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'flex-start',
            px: 4,
          },
          '& .MuiDataGrid-columnHeader': {
            px: 4,
          },
          '& .MuiDataGrid-cell.tags-edit-cell': {
            alignItems: 'center',
          },
          '& .MuiDataGrid-cell.tags-schedules-cell': {
            alignItems: 'flex-start',
            whiteSpace: 'normal',
            lineHeight: 1.4,
          },
          '& .tags-row--inactive': {
            opacity: 0.5,
          },
          ...DATAGRID_HIDE_SEPARATORS_SX,
        }}
      />
      <Dialog open={!!endTagId} onClose={endTag.isPending ? undefined : () => setEndTagId(null)}>
        <DialogTitle>End Tag</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to end this tag? All active schedules will have their end date set to now.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setEndTagId(null)} disabled={endTag.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (endTagId) {
                await endTag.mutateAsync(endTagId);
                setEndTagId(null);
              }
            }}
            disabled={endTag.isPending}
          >
            End Tag
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
